<script lang="ts" setup>
import { cva } from 'class-variance-authority';
import { cn } from '@/helpers/tailwind-helpers';

import BaseLabel, { BaseLabelProps } from '@/atoms/BaseLabel.vue';
import BaseTag, { BaseTagProps } from '@/atoms/BaseTag.vue';
import BaseButton, { BaseButtonProps } from '@/atoms/BaseButton.vue';
import { computed } from 'vue';
import LocalIcon, { LocalIconProps } from '@/atoms/LocalIcon.vue';
import CircleIcon from '@/atoms/CircleIcon.vue';

export type RowItemProps = {
  id: string | number;
  button?: BaseButtonProps;
  icon?: LocalIconProps;
  // TODO: remove in favor of icon props
  iconName?: string;
  imagePathLeft?: string;
  iconSize?: 'small' | 'medium';
  tag?: BaseTagProps;
  title: string;
  description?: string;
  descriptionProps?: BaseLabelProps;
  selected?: boolean;
  bordered?: boolean;
  color?:
    | 'white'
    | 'primary'
    | 'success'
    | 'secondary'
    | 'brand'
    | 'tertiary'
    | 'placeholder'
    | 'error';
  titleColor?: RowItemProps['color'];
  titleWrap?: boolean;
  class?: string;
};

const props = withDefaults(defineProps<RowItemProps>(), {
  iconSize: 'small',
  bordered: true,
});
const rowItem = cva('flex items-center space-x-2 py-3 px-4', {
  variants: {
    selected: {
      true: 'bg-brand-solid text-white rounded-xl',
      false: 'text-primary',
    },
    bordered: {
      true: 'border-b border-secondary',
      false: '',
    },
    color: {
      primary: 'text-primary',
      white: 'text-white',
      secondary: 'text-secondary',
      brand: 'text-brand-solid',
      tertiary: 'text-tertiary',
      placeholder: 'text-placeholder',
      error: 'text-error-solid',
      success: 'text-success',
    },
  },
  defaultVariants: {
    selected: false,
  },
});

const classes = computed(() => cn(rowItem(props), props.class));
const descriptionColor = computed(() => {
  if (props.selected) return 'white';
  return props.color || 'secondary';
});
</script>

<template>
  <article :class="classes">
    <BaseButton
      v-if="iconName"
      :icon-name="iconName"
      :size="iconSize"
      type="secondary"
      v-bind="icon"
    />
    <LocalIcon v-else-if="icon" v-bind="icon" />
    <CircleIcon
      v-if="imagePathLeft"
      :icon-name="imagePathLeft ? undefined : 'syringe'"
      :img-src="imagePathLeft"
    />
    <div class="flex flex-col mr-7 space-y-1 w-full min-w-0">
      <div class="flex justify-between space-x-2">
        <BaseLabel
          preset="medium"
          :text="title"
          :color="titleColor"
          :class="titleWrap ? 'break-words' : 'truncate whitespace-nowrap'"
        />
        <BaseTag v-if="tag" type="outline" size="small" class="shrink-0 mt-0.5 h-7" v-bind="tag" />
      </div>
      <BaseLabel
        v-if="description || descriptionProps"
        preset="small"
        :text="description"
        :color="descriptionColor"
        v-bind="descriptionProps"
      />
    </div>
    <div class="ml-auto shrink-0 flex items-center space-x-2">
      <slot />
    </div>
    <slot name="extra" />
  </article>
</template>
